import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, AlertTriangle, ArrowRight } from "lucide-react";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [state, setState] = useState({ phase: "checking", attempts: 0, data: null, error: null });
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", attempts: 0, data: null, error: "Missing session id" });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      if (stoppedRef.current) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "paid", attempts, data, error: null });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", attempts, data, error: null });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", attempts, data, error: null });
          return;
        }
        setState({ phase: "pending", attempts, data, error: null });
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setState({ phase: "error", attempts, data: null, error: e?.response?.data?.detail || e.message });
      }
    };
    poll();
    return () => { stoppedRef.current = true; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-20" data-testid="payment-success-page">
      <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center shadow-sm">
        {state.phase === "checking" || state.phase === "pending" ? (
          <>
            <Loader2 className="mx-auto text-[#1a2d5e] animate-spin" size={48} />
            <h1 className="font-display font-black text-3xl mt-6 text-[#0b1838]">Confirming your payment…</h1>
            <p className="text-slate-500 mt-2 text-sm">Don't close this page. This usually takes a few seconds.</p>
            <div className="mt-4 text-xs text-slate-400">Attempt {state.attempts}/{MAX_ATTEMPTS}</div>
          </>
        ) : state.phase === "paid" ? (
          <>
            <CheckCircle2 className="mx-auto text-emerald-500" size={56} />
            <h1 className="font-display font-black text-4xl mt-6 text-[#0b1838]">Payment successful</h1>
            <p className="text-slate-600 mt-3">Thank you for your order. We'll be in touch shortly to confirm delivery or in-store pickup.</p>
            {state.data?.product_title && (
              <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg text-left">
                <div className="overline">Order</div>
                <div className="font-display font-bold text-lg mt-1 text-[#0b1838]" data-testid="payment-product-title">{state.data.product_title}</div>
                <div className="text-sm text-slate-500 mt-1">{(state.data.amount_total / 100).toFixed(2)} {String(state.data.currency || "").toUpperCase()}</div>
              </div>
            )}
            <Link to="/shop" data-testid="payment-back-to-shop" className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-[#1a2d5e] text-white font-semibold rounded-lg hover:bg-[#0b1838]">Continue shopping <ArrowRight size={16} /></Link>
          </>
        ) : state.phase === "expired" ? (
          <>
            <XCircle className="mx-auto text-red-500" size={56} />
            <h1 className="font-display font-black text-3xl mt-6 text-[#0b1838]">Session expired</h1>
            <p className="text-slate-600 mt-3">This checkout link has expired. Please try again.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-[#1a2d5e] text-white font-semibold rounded-lg hover:bg-[#0b1838]">Back to shop</Link>
          </>
        ) : state.phase === "timeout" ? (
          <>
            <AlertTriangle className="mx-auto text-amber-500" size={56} />
            <h1 className="font-display font-black text-3xl mt-6 text-[#0b1838]">Still processing…</h1>
            <p className="text-slate-600 mt-3">Your payment is taking longer than usual to confirm. We'll send a confirmation email as soon as it's done.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 mt-8 px-6 py-3 border border-slate-300 font-semibold rounded-lg hover:bg-slate-50">Back to shop</Link>
          </>
        ) : (
          <>
            <XCircle className="mx-auto text-red-500" size={56} />
            <h1 className="font-display font-black text-3xl mt-6 text-[#0b1838]">Something went wrong</h1>
            <p className="text-slate-600 mt-3">{state.error || "Could not confirm payment."}</p>
            <Link to="/shop" className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-[#1a2d5e] text-white font-semibold rounded-lg hover:bg-[#0b1838]">Back to shop</Link>
          </>
        )}
      </div>
    </div>
  );
}
